import React, { useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, HelpCircle, Save, X, Search, Filter, ArrowUp, ArrowDown, Copy } from 'lucide-react';

export default function FAQConfigSection({ faqs, onChange }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [newFAQ, setNewFAQ] = useState({ question: '', answer: '', category: 'general', priority: 'medium' });
  const [isAdding, setIsAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const categories = [
    { value: 'general', label: 'Général', color: 'bg-gray-100 text-gray-800' },
    { value: 'pricing', label: 'Tarifs', color: 'bg-blue-100 text-blue-800' },
    { value: 'shipping', label: 'Expédition', color: 'bg-green-100 text-green-800' },
    { value: 'tracking', label: 'Suivi', color: 'bg-purple-100 text-purple-800' },
    { value: 'documents', label: 'Documents', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'products', label: 'Produits', color: 'bg-pink-100 text-pink-800' },
    { value: 'delivery', label: 'Livraison', color: 'bg-indigo-100 text-indigo-800' }
  ];

  const priorities = [
    { value: 'high', label: 'Haute', color: 'text-red-600', icon: '🔴' },
    { value: 'medium', label: 'Moyenne', color: 'text-orange-600', icon: '🟡' },
    { value: 'low', label: 'Basse', color: 'text-green-600', icon: '🟢' }
  ];

  const filteredFAQs = useMemo(() => {
    let filtered = [...faqs];

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(faq => 
        faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered?.filter(faq => faq?.category === filterCategory);
    }

    // Priority filter
    if (filterPriority !== 'all') {
      filtered = filtered?.filter(faq => faq?.priority === filterPriority);
    }

    // Sort by priority (high -> medium -> low)
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    filtered?.sort((a, b) => {
      const priorityA = priorityOrder?.[a?.priority || 'medium'];
      const priorityB = priorityOrder?.[b?.priority || 'medium'];
      return priorityA - priorityB;
    });

    return filtered;
  }, [faqs, searchQuery, filterCategory, filterPriority]);

  const handleAdd = () => {
    if (newFAQ?.question?.trim() && newFAQ?.answer?.trim()) {
      onChange([...faqs, { 
        ...newFAQ, 
        id: Date.now(),
        category: newFAQ?.category || 'general',
        priority: newFAQ?.priority || 'medium'
      }]);
      setNewFAQ({ question: '', answer: '', category: 'general', priority: 'medium' });
      setIsAdding(false);
    }
  };

  const handleEdit = (index, field, value) => {
    const updated = [...faqs];
    const actualIndex = faqs?.findIndex(f => f?.id === filteredFAQs?.[index]?.id);
    updated[actualIndex] = { ...updated?.[actualIndex], [field]: value };
    onChange(updated);
  };

  const handleDelete = (index) => {
    const faqToDelete = filteredFAQs?.[index];
    const updated = faqs?.filter(f => f?.id !== faqToDelete?.id);
    onChange(updated);
  };

  const handleDuplicate = (index) => {
    const faqToDuplicate = filteredFAQs?.[index];
    const duplicated = {
      ...faqToDuplicate,
      id: Date.now(),
      question: `${faqToDuplicate?.question} (copie)`
    };
    onChange([...faqs, duplicated]);
  };

  const handleMovePriority = (index, direction) => {
    const faq = filteredFAQs?.[index];
    const actualIndex = faqs?.findIndex(f => f?.id === faq?.id);
    const updated = [...faqs];
    const currentPriority = faq?.priority || 'medium';
    const priorities = ['low', 'medium', 'high'];
    const currentIndex = priorities?.indexOf(currentPriority);
    const newIndex = direction === 'up' ? Math.min(currentIndex + 1, 2) : Math.max(currentIndex - 1, 0);
    updated[actualIndex] = { ...updated?.[actualIndex], priority: priorities?.[newIndex] };
    onChange(updated);
  };

  const getCategoryColor = (category) => {
    return categories?.find(c => c?.value === category)?.color || 'bg-gray-100 text-gray-800';
  };

  const getPriorityInfo = (priority) => {
    return priorities?.find(p => p?.value === priority) || priorities?.[1];
  };

  const stats = {
    total: faqs?.length,
    high: faqs?.filter(f => f?.priority === 'high')?.length,
    medium: faqs?.filter(f => f?.priority === 'medium')?.length,
    low: faqs?.filter(f => f?.priority === 'low')?.length
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center space-x-2">
            <HelpCircle className="text-green-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">FAQ Personnalisées</h2>
          </div>
          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
            <span>Total: <strong>{stats?.total}</strong></span>
            <span className="text-red-600">🔴 Haute: <strong>{stats?.high}</strong></span>
            <span className="text-orange-600">🟡 Moyenne: <strong>{stats?.medium}</strong></span>
            <span className="text-green-600">🟢 Basse: <strong>{stats?.low}</strong></span>
          </div>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus size={18} />
          <span>Ajouter FAQ</span>
        </button>
      </div>
      <p className="text-gray-600 text-sm mb-6">
        Créez des questions-réponses fréquentes que Fatou utilisera pour répondre aux clients. Organisez-les par catégorie et priorité.
      </p>
      {/* Search and Filters */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Rechercher dans les questions et réponses..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <Filter className="text-gray-400" size={20} />
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">Toutes catégories</option>
            {categories?.map(cat => (
              <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
            ))}
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">Toutes priorités</option>
            {priorities?.map(pri => (
              <option key={pri?.value} value={pri?.value}>{pri?.icon} {pri?.label}</option>
            ))}
          </select>
          {(searchQuery || filterCategory !== 'all' || filterPriority !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterCategory('all');
                setFilterPriority('all');
              }}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>
      {/* Add New FAQ Form */}
      {isAdding && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <h3 className="font-medium text-gray-900 mb-3">Nouvelle FAQ</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie
                </label>
                <select
                  value={newFAQ?.category}
                  onChange={(e) => setNewFAQ({ ...newFAQ, category: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {categories?.map(cat => (
                    <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priorité
                </label>
                <select
                  value={newFAQ?.priority}
                  onChange={(e) => setNewFAQ({ ...newFAQ, priority: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {priorities?.map(pri => (
                    <option key={pri?.value} value={pri?.value}>{pri?.icon} {pri?.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question
              </label>
              <input
                type="text"
                value={newFAQ?.question}
                onChange={(e) => setNewFAQ({ ...newFAQ, question: e?.target?.value })}
                placeholder="Ex: Quel est le délai de livraison?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Réponse
              </label>
              <textarea
                value={newFAQ?.answer}
                onChange={(e) => setNewFAQ({ ...newFAQ, answer: e?.target?.value })}
                placeholder="Réponse détaillée avec le style Fatou (familier, nouchi, emojis)..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAdd}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Save size={16} />
                <span>Enregistrer</span>
              </button>
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewFAQ({ question: '', answer: '', category: 'general', priority: 'medium' });
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <X size={16} />
                <span>Annuler</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* FAQ List */}
      <div className="space-y-4">
        {filteredFAQs?.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <HelpCircle size={48} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600">
              {searchQuery || filterCategory !== 'all' || filterPriority !== 'all' ?'Aucune FAQ ne correspond aux filtres' :'Aucune FAQ configurée'
              }
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {searchQuery || filterCategory !== 'all' || filterPriority !== 'all' ?'Essayez de modifier vos critères de recherche' :'Cliquez sur "Ajouter FAQ" pour commencer'
              }
            </p>
          </div>
        ) : (
          filteredFAQs?.map((faq, index) => {
            const priorityInfo = getPriorityInfo(faq?.priority);
            const actualIndex = faqs?.findIndex(f => f?.id === faq?.id);
            const isEditing = editingIndex === actualIndex;
            
            return (
              <div key={faq?.id || index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Catégorie
                        </label>
                        <select
                          value={faq?.category || 'general'}
                          onChange={(e) => handleEdit(index, 'category', e?.target?.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          {categories?.map(cat => (
                            <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priorité
                        </label>
                        <select
                          value={faq?.priority || 'medium'}
                          onChange={(e) => handleEdit(index, 'priority', e?.target?.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          {priorities?.map(pri => (
                            <option key={pri?.value} value={pri?.value}>{pri?.icon} {pri?.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question
                      </label>
                      <input
                        type="text"
                        value={faq?.question}
                        onChange={(e) => handleEdit(index, 'question', e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Réponse
                      </label>
                      <textarea
                        value={faq?.answer}
                        onChange={(e) => handleEdit(index, 'answer', e?.target?.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingIndex(null)}
                        className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <Save size={14} />
                        <span>Enregistrer</span>
                      </button>
                      <button
                        onClick={() => setEditingIndex(null)}
                        className="flex items-center space-x-2 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                      >
                        <X size={14} />
                        <span>Annuler</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(faq?.category)}`}>
                            {categories?.find(c => c?.value === faq?.category)?.label || 'Général'}
                          </span>
                          <span className={`text-sm font-medium ${priorityInfo?.color}`}>
                            {priorityInfo?.icon} {priorityInfo?.label}
                          </span>
                        </div>
                        <h3 className="font-medium text-gray-900">{faq?.question}</h3>
                      </div>
                      <div className="flex space-x-1 ml-4">
                        <button
                          onClick={() => handleMovePriority(index, 'up')}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                          title="Augmenter priorité"
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          onClick={() => handleMovePriority(index, 'down')}
                          className="p-1 text-gray-400 hover:text-orange-600 transition-colors"
                          title="Diminuer priorité"
                        >
                          <ArrowDown size={16} />
                        </button>
                        <button
                          onClick={() => handleDuplicate(index)}
                          className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                          title="Dupliquer"
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          onClick={() => setEditingIndex(actualIndex)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(index)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm whitespace-pre-wrap">{faq?.answer}</p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <p className="text-sm text-blue-800">
          <strong>💡 Conseils:</strong>
        </p>
        <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
          <li><strong>Priorité Haute (🔴):</strong> Questions critiques (tarifs, délais, documents obligatoires)</li>
          <li><strong>Priorité Moyenne (🟡):</strong> Questions fréquentes mais non urgentes</li>
          <li><strong>Priorité Basse (🟢):</strong> Questions secondaires ou informatives</li>
          <li>Utilisez le style Fatou: familier, nouchi ivoirien, emojis, expressions neutres (boss, chef, ami)</li>
        </ul>
      </div>
    </div>
  );
}